import React, { useEffect, useState } from "react";
import APIService from "../../../modules/apiService";
import Spinner from "../../../components/spinner";
import { useBillingContext } from "..";
import { formatDate, formatMoney, formatNumber } from "../../../utils/formatters";
import moment from "moment-timezone";

const api = new APIService();

const INVOICE_TYPE = {
  InvoiceA: 1,
  DebitNoteA: 2,
  CreditNoteA: 3,
  InvoiceB: 6,
  DebitNoteB: 7,
  CreditNoteB: 8,
  InvoiceC: 11,
  DebitNoteC: 12,
  CreditNoteC: 13,
};

const STATUS = {
  pending: {
    label: "Pendiente",
    className: "info",
  },
  approved: {
    label: "Aprobado",
    className: "success",
  },
  rejected: {
    label: "Rechazado",
    className: "danger",
  },
  cancelled: {
    label: "Anulado",
    className: "danger",
  },
};

export default function Invoices() {
  const { showErrorModal } = useBillingContext();
  const [isLoading, setIsLoading] = useState(true);
  const [invoices, setInvoices] = useState(null);
  const [processingInvoiceId, setProcessingInvoiceId] = useState(null);

  const params = Object.fromEntries(new URLSearchParams(window.location.search));

  const [filterForm, setFilterForm] = useState({
    q: params.q || "",
    start: params.start || moment().subtract(3, "month").format("YYYY-MM-01"),
    end: params.end || moment().format("YYYY-MM-DD"),
  });

  const [filters, setFilters] = useState({...filterForm});

  const handleFilterFormChange = (e) => {
    setFilterForm({...filterForm, [e.target.name]: e.target.value});
  };

  const handleApproveInvoice = async (invoice) => {
    setProcessingInvoiceId(invoice.id);
    try {
      const response = await api.postApproveInvoice(invoice.id);

      if (response.ok) {
        const updatedInvoice = await response.json();
        setInvoices((prevInvoices) => prevInvoices.map((inv) => (inv.id === invoice.id ? { ...inv, invoice_number: updatedInvoice.invoice_number, status: updatedInvoice.status } : inv)));
      } else {
        const { message } = await response.json();
        throw new Error(message);
      }
    } catch (err) {
      console.error(err);
      showErrorModal(err.message);
    } finally {
      setProcessingInvoiceId(null);
    }
  };

  const handleCancelInvoice = async (invoice) => {
    if (!window.confirm(`¿Está seguro de que desea anular la ${invoice.invoice_type_description} ${invoice.letter} ${invoice.invoice_number}?`)) {
      return;
    }

    setProcessingInvoiceId(invoice.id);
    try {
      const response = await api.deleteInvoice(invoice.id);

      if (response.ok) {
        await fetchInvoices();
      } else {
        const { message } = await response.json();
        throw new Error(message);
      }
    } catch (err) {
      console.error(err);
      showErrorModal(err.message);
    } finally {
      setProcessingInvoiceId(null);
    }
  };

  const fetchInvoices = async () => {
    setIsLoading(true);

    try {
      const response = await api.getInvoices({
        ...filters,
      });

      if (response.ok) {
        const { data } = await response.json();
        setInvoices(data);
      } else {
        const { message } = await response.json();
        throw new Error(`Error al obtener los facturas: ${message}`);
      }
    } catch (err) {
      console.error(err);
      showErrorModal(err.message);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchInvoices();
  }, [filters]);

  return (
    <>
      <form
        className="form-inline m-3"
        onSubmit={(event) => {
          event.preventDefault();

          window.history.replaceState(null, "", `${window.location.pathname}?${new URLSearchParams(filterForm)}`);

          setFilters({...filterForm});
        }}
      >
        <input type="search" name="q" className="form-control mb-2 mr-sm-2" placeholder="Buscar documento o nombre..."
               value={filterForm.q} onChange={handleFilterFormChange} style={{width: '260px'}}/>
        <input type="date" name="start" className="form-control mb-2 mr-sm-2" value={filterForm.start}
               onChange={handleFilterFormChange} max={filterForm.end}/>
        <input type="date" name="end" className="form-control mb-2 mr-sm-2" value={filterForm.end} onChange={handleFilterFormChange}
               min={filterForm.start}/>
        <button type="submit" className="btn btn-primary mb-2 mr-sm-2">
          Filtrar
        </button>
      </form>
      {isLoading && <Spinner />}
      {invoices?.length === 0 && <div className="p-3 text-muted text-center">No se encontraron comprobantes</div>}
      {invoices?.length > 0 && (
        <table className="table table-hover m-0">
          <thead>
            <tr>
              <th className="text-right">Fecha</th>
              <th>Tipo</th>
              <th>Número</th>
              <th>Paciente</th>
              <th>Documento</th>
              <th>Detalle</th>
              <th className="text-right">Importe</th>
              <th className="text-center">Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice, index) => (
              <tr key={index}>
                <td className="text-right">{formatDate(invoice.invoice_date)}</td>
                <td>{invoice.invoice_type_description}</td>
                <td>{invoice.invoice_number ? `${invoice.letter} ${invoice.invoice_number}` : <small className="text-muted">Sin número</small>}</td>
                <td>{invoice.patient_name}</td>
                <td>{`${invoice.patient_identification_type_description} ${formatNumber(invoice.patient_identification_number, 0)}`}</td>
                <td>{invoice.detail}</td>
                <td className="text-right">{formatMoney(invoice.amount_total)}</td>
                <td className="text-center">
                  <span className={`badge badge-${STATUS[invoice.status].className}`}>{STATUS[invoice.status].label}</span>
                </td>
                <td className="text-right d-flex justify-content-end align-items-center">
                  {invoice.status === "pending" && (
                    <button type="button" className="btn btn-sm btn-success mr-2" onClick={() => handleApproveInvoice(invoice)} disabled={processingInvoiceId !== null}>
                      {processingInvoiceId === invoice.id ? (
                        <div className="d-flex align-items-center" style={{ gap: "5px" }}>
                          <div className="spinner-border spinner-border-sm" role="status" />
                          Aprobando...
                        </div>
                      ) : (
                        "Aprobar"
                      )}
                    </button>
                  )}
                  {invoice.status === "approved" && [INVOICE_TYPE.InvoiceA, INVOICE_TYPE.InvoiceB, INVOICE_TYPE.InvoiceC].includes(invoice.invoice_type) && (
                    <button type="button" className="btn btn-sm btn-danger mr-2" onClick={() => handleCancelInvoice(invoice)} disabled={processingInvoiceId !== null}>
                      {processingInvoiceId === invoice.id ? (
                        <div className="d-flex align-items-center" style={{ gap: "5px" }}>
                          <div className="spinner-border spinner-border-sm" role="status" />
                          Anulando...
                        </div>
                      ) : (
                        "Anular"
                      )}
                    </button>
                  )}
                  {invoice.pdf_url && (
                    <a href={invoice.pdf_url} target="_blank" className="btn btn-sm btn-primary">
                      Descargar
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
